
import React, { useState } from 'react';
import { Review } from '@/hooks/useReviews';
import { Star, Clock, Trash2, MessageSquare } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from './ui/button';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback } from './ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from './ui/badge';
import { toast } from '@/components/ui/use-toast';
import { useReviewReplies } from '@/hooks/useReviewReplies';
import ReviewReplyForm from './ReviewReplyForm';
import ReviewReplyDisplay from './ReviewReplyDisplay';

interface ReviewListProps {
  reviews: Review[];
  isLoading: boolean;
  error: Error | null;
  showLoadMore?: boolean;
  onDeleteReview?: (reviewId: string) => void;
}

const ReviewList: React.FC<ReviewListProps> = ({ 
  reviews, 
  isLoading, 
  error,
  showLoadMore = false,
  onDeleteReview
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [replyFormStates, setReplyFormStates] = useState<Record<string, boolean>>({});

  const renderRatingStars = (rating: number) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star 
            key={star}
            size={14}
            className={cn(
              "stroke-[1.5]",
              star <= rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
            )}
          />
        ))}
        <span className="ml-1 text-xs font-medium text-gray-900">{rating.toFixed(1)}</span>
      </div>
    );
  };

  const getInitials = (name: string) => {
    if (!name) return '';
    
    const parts = name.split(' ');
    if (parts.length === 1) return parts[0].charAt(0);
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`;
  };

  const getDisplayName = (review: Review) => {
    return review.display_name || review.authorName || 'Anonymous User';
  };

  const handleDelete = (reviewId: string) => {
    if (onDeleteReview) {
      onDeleteReview(reviewId);
    }
  };

  const toggleReplyForm = (reviewId: string) => {
    setReplyFormStates(prev => ({
      ...prev,
      [reviewId]: !prev[reviewId]
    }));
  };

  const closeReplyForm = (reviewId: string) => {
    setReplyFormStates(prev => ({
      ...prev,
      [reviewId]: false
    }));
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-gray-50 border border-gray-100 rounded-xl p-6 animate-pulse shadow-sm">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-gray-200" />
              <div className="flex-1 space-y-3">
                <div className="h-4 bg-gray-200 rounded w-32" />
                <div className="h-3 bg-gray-200 rounded w-20" />
                <div className="h-16 bg-gray-200 rounded w-full" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 bg-red-50 border border-red-100 rounded-xl">
        <p className="text-red-600 font-medium mb-2">Error loading reviews</p>
        <p className="text-red-500 text-sm">{error.message}</p>
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="text-center py-8 bg-white border border-gray-100 rounded-xl">
        <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <Star className="w-8 h-8 text-gray-400" />
        </div>
        <p className="text-gray-600 text-sm mb-1">{t('service.details.no_reviews_yet')}</p>
        <p className="text-xs text-gray-500">{t('service.details.be_first_to_review')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {reviews.map((review) => {
        const displayName = getDisplayName(review);
        const isUserReview = user && user.id === review.user_id;
        const isPending = review.status === "Pending Validation";
        const isPublished = review.status === "Published";
        const showReplyForm = replyFormStates[review.id];
        
        return (
          <ReviewItem
            key={review.id}
            review={review}
            displayName={displayName}
            isUserReview={isUserReview}
            isPending={isPending}
            isPublished={isPublished}
            showReplyForm={showReplyForm}
            onToggleReplyForm={() => toggleReplyForm(review.id)}
            onCloseReplyForm={() => closeReplyForm(review.id)}
            onDeleteReview={handleDelete}
            renderRatingStars={renderRatingStars}
            getInitials={getInitials}
          />
        );
      })}
      
      {showLoadMore && reviews.length > 3 && (
        <div className="pt-4">
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full border-gray-200 hover:bg-gray-50 transition-colors duration-200"
          >
            See all {reviews.length} reviews
          </Button>
        </div>
      )}
    </div>
  );
};

// Enhanced ReviewItem component with better styling
const ReviewItem: React.FC<{
  review: Review;
  displayName: string;
  isUserReview: boolean;
  isPending: boolean;
  isPublished: boolean;
  showReplyForm: boolean;
  onToggleReplyForm: () => void;
  onCloseReplyForm: () => void;
  onDeleteReview: (reviewId: string) => void;
  renderRatingStars: (rating: number) => React.ReactNode;
  getInitials: (name: string) => string;
}> = ({
  review,
  displayName,
  isUserReview,
  isPending,
  isPublished,
  showReplyForm,
  onToggleReplyForm,
  onCloseReplyForm,
  onDeleteReview,
  renderRatingStars,
  getInitials
}) => {
  const { user } = useAuth();
  const { 
    reply, 
    canReply, 
    createReply, 
    updateReply, 
    deleteReply 
  } = useReviewReplies(review.id);

  const [editingReply, setEditingReply] = useState(false);

  const handleReplySubmit = async (content: string) => {
    if (reply && editingReply) {
      await updateReply.mutateAsync({ replyId: reply.id, content });
      setEditingReply(false);
    } else {
      await createReply.mutateAsync({ reviewId: review.id, content });
    }
    onCloseReplyForm();
  };

  const handleEditReply = () => {
    setEditingReply(true);
    onToggleReplyForm();
  };

  const handleDeleteReply = async () => {
    if (reply) {
      await deleteReply.mutateAsync(reply.id);
    }
  };

  const canEditReply = reply && reply.status === 'Pending Validation' && canReply;
  const canDeleteReply = reply && reply.status === 'Pending Validation' && canReply;

  return (
    <div 
      className={cn(
        "bg-white border border-gray-100 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300",
        isPending && "bg-amber-50 border-amber-200"
      )}
    >
      {/* Review Header */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-3">
          <Avatar className="w-10 h-10 ring-2 ring-white shadow-sm flex-shrink-0">
            <AvatarFallback className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground font-semibold text-sm">
              {getInitials(displayName)}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-h-[40px] flex flex-col justify-center">
            <div className="font-semibold text-gray-900 text-sm leading-tight">{displayName}</div>
            <div className="flex items-center mt-0.5">
              {renderRatingStars(review.rating)}
            </div>
          </div>
        </div>
        
        <div className="flex flex-col items-end gap-2">
          {/* Date moved to top right */}
          <span className="text-[11px] text-gray-500">
            {formatDistanceToNow(new Date(review.created_at), { addSuffix: true })}
          </span>
          
          <div className="flex items-center gap-2">
            {isPending && (
              <Badge variant="outline" className="flex items-center text-amber-600 border-amber-300 bg-amber-50 text-xs">
                <Clock className="w-3 h-3 mr-1" />
                <span>Pending</span>
              </Badge>
            )}
            
            {isUserReview && onDeleteReview && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Review</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete your review? This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={() => onDeleteReview(review.id)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </div>
      
      {/* Review Content - Full width */}
      <div className="prose prose-sm max-w-none">
        <p className="text-gray-700 leading-relaxed">{review.description}</p>
      </div>

      {/* Business Reply Section */}
      {reply && !showReplyForm && (
        <ReviewReplyDisplay
          reply={reply}
          canEdit={!!canEditReply}
          canDelete={!!canDeleteReply}
          onEdit={handleEditReply}
          onDelete={handleDeleteReply}
        />
      )}

      {/* Reply Actions */}
      {isPublished && !reply && canReply && !showReplyForm && (
        <div className="mt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={onToggleReplyForm}
            className="flex items-center gap-2"
          >
            <MessageSquare className="w-4 h-4" />
            Reply as Business
          </Button>
        </div>
      )}

      {/* Reply Form */}
      {showReplyForm && (
        <div className="mt-4">
          <ReviewReplyForm
            reviewId={review.id}
            existingReply={editingReply ? reply : null}
            businessName={reply?.business_name || 'Loading...'}
            onSubmit={handleReplySubmit}
            onCancel={() => {
              onCloseReplyForm();
              setEditingReply(false);
            }}
            isSubmitting={createReply.isPending || updateReply.isPending}
          />
        </div>
      )}
    </div>
  );
};

export default ReviewList;
